import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import type { SystemSettings } from '../../types';
import {
  Settings,
  Save,
  AlertCircle,
  Shield,
  Clock,
  Users,
  Flag,
  Lock,
  Unlock,
  RefreshCw
} from 'lucide-react';

export const SystemSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSystemSettings();
      setSettings(response);
      setIsMaintenanceMode(response.maintenance_mode);
      setMaintenanceMessage(response.maintenance_message);
    } catch (err: any) {
      setError(err.message || 'Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true);
      await adminAPI.updateSystemSettings({
        maintenance_mode: isMaintenanceMode,
        maintenance_message: maintenanceMessage
      });
      await fetchSettings();
    } catch (err: any) {
      setError(err.message || 'Failed to save system settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleMaintenanceMode = async () => {
    try {
      setIsSaving(true);
      await adminAPI.toggleMaintenanceMode();
      await fetchSettings();
    } catch (err: any) {
      setError(err.message || 'Failed to toggle maintenance mode');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner h-8 w-8"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">System Settings</h1>
          <button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? (
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Save className="w-5 h-5 mr-2" />
            )}
            Save Changes
          </button>
        </div>

        <div className="space-y-6">
          {/* Maintenance Mode */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Shield className="w-5 h-5 text-blue-400 mr-2" />
                <h3 className="text-lg font-semibold text-white">Maintenance Mode</h3>
              </div>
              <button
                onClick={handleToggleMaintenanceMode}
                disabled={isSaving}
                className={`flex items-center px-4 py-2 rounded-lg ${
                  isMaintenanceMode
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                } disabled:opacity-50`}
              >
                {isMaintenanceMode ? (
                  <>
                    <Unlock className="w-4 h-4 mr-2" />
                    Disable Maintenance Mode
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Enable Maintenance Mode
                  </>
                )}
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Maintenance Message
                </label>
                <textarea
                  value={maintenanceMessage}
                  onChange={(e) => setMaintenanceMessage(e.target.value)}
                  placeholder="Enter maintenance message..."
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="flex items-center text-sm text-gray-400">
                <Clock className="w-4 h-4 mr-2" />
                Last updated: {settings?.updated_at ? new Date(settings.updated_at).toLocaleString() : 'Never'}
              </div>
            </div>
          </div>

          {/* System Information */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center mb-6">
              <Settings className="w-5 h-5 text-blue-400 mr-2" />
              <h3 className="text-lg font-semibold text-white">System Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center text-sm text-gray-400 mb-1">
                  <Users className="w-4 h-4 mr-2" />
                  Total Users
                </div>
                <div className="text-2xl font-semibold text-white">{settings?.total_users || 0}</div>
              </div>

              <div className="bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center text-sm text-gray-400 mb-1">
                  <Flag className="w-4 h-4 mr-2" />
                  Total Challenges
                </div>
                <div className="text-2xl font-semibold text-white">{settings?.total_challenges || 0}</div>
              </div>

              <div className="bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center text-sm text-gray-400 mb-1">
                  <Clock className="w-4 h-4 mr-2" />
                  System Uptime
                </div>
                <div className="text-2xl font-semibold text-white">{settings?.system_uptime || '0h 0m'}</div>
              </div>

              <div className="bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center text-sm text-gray-400 mb-1">
                  <Shield className="w-4 h-4 mr-2" />
                  System Version
                </div>
                <div className="text-2xl font-semibold text-white">{settings?.version || '1.0.0'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 